import axios from "axios";
import { saveAs } from "file-saver";
import type { ToastVariant } from "../Methods/SnackBar";

export function serializeDownloadParams(params: Record<string, unknown>): string {
	const parts: string[] = [];
	for (const [key, val] of Object.entries(params)) {
		if (val === undefined || val === null) continue;
		if (Array.isArray(val)) {
			for (const item of val) {
				parts.push(
					`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`
				);
			}
		} else {
			parts.push(
				`${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`
			);
		}
	}
	return parts.join("&");
}

export async function parseBlobErrorMessage(data: unknown): Promise<string> {
	if (!(data instanceof Blob)) return "Export failed";
	const text = await data.text();
	try {
		const j = JSON.parse(text) as { error?: string; detail?: string };
		return j.error || j.detail || text || "Export failed";
	} catch {
		return text || "Export failed";
	}
}

export function normalizeEmployeeName(name: string): string {
	return name.trim().replace(/\s+/g, " ");
}

export function buildExportNameToIdMap(
	teamEmployees: Array<{ employee_name?: string; employee_id?: unknown }> | undefined,
	computedRows: Array<{ employee_name?: unknown; employee_id?: unknown }>
): Map<string, string> {
	const map = new Map<string, string>();
	const put = (rawName: unknown, rawId: unknown) => {
		if (rawName == null || rawId == null || rawId === "") return;
		const key = normalizeEmployeeName(String(rawName));
		if (!key) return;
		if (!map.has(key)) map.set(key, String(rawId));
	};
	for (const e of teamEmployees ?? []) {
		put(e.employee_name, e.employee_id);
	}
	for (const row of computedRows) {
		put(row.employee_name, row.employee_id);
	}
	return map;
}

const BAR_CHART_IGNORE_KEYS = new Set([
	"No Assignees",
	"No Reporters",
]);

export function barChartSeriesNames(computedData: any[]): string[] {
	if (!computedData?.length) return [];
	const keys = new Set<string>();
	for (const row of computedData) {
		for (const k of Object.keys(row)) {
			if (
				k !== "week" &&
				k !== "__jiraTooltip" &&
				!BAR_CHART_IGNORE_KEYS.has(k)
			) {
				keys.add(k);
			}
		}
	}
	return Array.from(keys);
}

export function resolveJiraExportEmployeeIds(options: {
	selectedEmployees: string[];
	chartEmployeeNames: string[];
	teamEmployees?: Array<{ employee_name?: string; employee_id?: unknown }>;
	rowsForIdFallback?: Array<{ employee_name?: unknown; employee_id?: unknown }>;
}): string[] | null {
	const {
		selectedEmployees,
		chartEmployeeNames,
		teamEmployees,
		rowsForIdFallback = [],
	} = options;
	if (selectedEmployees.length === 0) {
		throw new Error("Select at least one employee to export");
	}
	const fullTeamExport =
		selectedEmployees.length === chartEmployeeNames.length &&
		chartEmployeeNames.every((n) => selectedEmployees.includes(n));
	if (fullTeamExport) {
		return null;
	}
	const nameToId = buildExportNameToIdMap(teamEmployees, rowsForIdFallback);
	const exportIds = selectedEmployees.map((name) =>
		nameToId.get(normalizeEmployeeName(name))
	);
	if (exportIds.some((id) => id == null || id === "")) {
		throw new Error(
			"Could not resolve employee IDs for export; refresh and try again."
		);
	}
	return exportIds as string[];
}

export async function jiraChartBlobDownload(
	url: string,
	params: Record<string, unknown>,
	filename: string,
	showToast: (
		message: string,
		variant: ToastVariant,
		isCloseable: boolean
	) => void
): Promise<void> {
	const token = localStorage.getItem("token");
	try {
		const response = await axios.get(url, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params,
			paramsSerializer: {
				serialize: serializeDownloadParams,
			},
			responseType: "blob",
		});
		saveAs(response.data, filename);
	} catch (error: any) {
		console.error("Error downloading file:", error);
		if (error?.response?.status === 401) {
			window.location.href = "login";
			return;
		}
		if (error?.response?.data instanceof Blob) {
			const msg = await parseBlobErrorMessage(error.response.data);
			showToast(msg, "error", true);
		} else {
			showToast(error?.message ?? String(error), "error", true);
		}
	}
}
